import { ManualPaymentType, PaymentMethodType, PaymentStatus } from '../enums/data.enums';
import { PaymentHistoryModel } from '../models/data/payment-history.model';

const MANUAL_PAYMENT_TYPE_LABELS: Record<ManualPaymentType, string> = {
	[ManualPaymentType.ACH]: 'ACH',
	[ManualPaymentType.Card]: 'Card',
	[ManualPaymentType.Cash]: 'Cash',
	[ManualPaymentType.Check]: 'Check',
	[ManualPaymentType.Other]: 'Other',
};

export class PaymentUtilities {
	public static formatPaymentMethod(payment: PaymentHistoryModel): string {
		if (payment.isManualPayment) {
			const typeLabel = payment.manualPaymentType
				? MANUAL_PAYMENT_TYPE_LABELS[payment.manualPaymentType] ?? payment.manualPaymentType
				: 'Manual';

			if (payment.manualPaymentType === ManualPaymentType.Check && payment.checkNumber) {
				return `${typeLabel} #${payment.checkNumber}`;
			}

			return typeLabel;
		}

		switch (payment.paymentMethodType) {
			case PaymentMethodType.Card:
				return payment.paymentMethodLast4
					? `Card •••• ${payment.paymentMethodLast4}`
					: 'Card';

			case PaymentMethodType.AchDebit:
				return payment.paymentMethodLast4
					? `ACH •••• ${payment.paymentMethodLast4}`
					: 'ACH';

			default:
				return 'Unknown';
		}
	}

	// Only a Pending ACH payment can be canceled: card payments settle too fast, and anything past
	// Pending is already on its way (or done). Beyond that the viewer needs payments.cancel for the
	// organization, or must be the member who made the payment — a co-owner who didn't pay can't
	// cancel it (DEV-1866). Mirrors the Core API's own checks.
	public static canCancelPayment(
		payment: PaymentHistoryModel,
		hasCancelPermission: boolean,
		viewerMemberIds: readonly number[]
	): boolean {
		if (payment.isManualPayment) return false;
		if (payment.status !== PaymentStatus.Pending) return false;
		if (payment.paymentMethodType !== PaymentMethodType.AchDebit) return false;

		if (hasCancelPermission) return true;

		return payment.payerMemberId != null && viewerMemberIds.includes(payment.payerMemberId);
	}

	public static getEffectivePaymentId(payment: PaymentHistoryModel): number {
		return (payment.isManualPayment ? payment.manualPaymentId : payment.paymentId) ?? 0;
	}

	public static getChargeNameDisplay(payment: PaymentHistoryModel): string {
		if (!payment.charges || payment.charges.length === 0) return '';

		const firstName = payment.charges[0].name ?? '';
		if (payment.charges.length === 1) return firstName;

		return `${payment.charges.length} Invoices`;
	}

	public static getInvoiceNumberDisplay(payment: PaymentHistoryModel): string {
		if (!payment.charges || payment.charges.length === 0) return '';

		const firstId = payment.charges[0].propertyChargeId;
		if (payment.charges.length === 1) return String(firstId);

		return "";
	}
}
